import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { keyframes } from 'styled-components';

// Define the fadeInUp animation here to make the component self-contained
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface ScrollAnimationProps {
  children: React.ReactNode;
  delay?: number; // Delay in seconds
  threshold?: number; // Threshold for when the animation should trigger (0-1)
  className?: string;
}

const AnimationContainer = styled.div<{ $isVisible: boolean; $delay: number }>`
  opacity: 0;
  transform: translateY(20px);
  
  &.visible {
    animation: ${fadeInUp} 0.5s ease forwards;
    animation-delay: ${props => props.$delay}s;
  }
`;

const ScrollAnimation: React.FC<ScrollAnimationProps> = ({ 
  children, 
  delay = 0, 
  threshold = 0.1,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 100px 0px', // Trigger animation when element is 100px below viewport
        threshold
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return (
    <AnimationContainer 
      ref={ref} 
      $isVisible={isVisible} 
      $delay={delay}
      className={`${className} ${isVisible ? 'visible' : ''}`}
    >
      {children}
    </AnimationContainer>
  );
};

export default ScrollAnimation; 